<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineAsyncComponent, nextTick, ref, useTemplateRef, watch } from 'vue';

const LazyGridLayoutGridDataDebug = defineAsyncComponent(() => import('#layers/blocks/components/GridLayout/GridDataDebug.vue'));

const __DsBlock = {
	type: 'container',
	name: '$t:blocks.grid_layout.name',
	icon: 'lucide:layout-grid',
	description: '$t:blocks.grid_layout.description',
	category: 'layout',
	tags: ['layout', 'form-container'],
	addButtonsDisabled: true,
	options: [
		{
			name: 'horizontalAlign',
			label: '$t:blocks.grid_layout.props.horizontal_align',
			block: BuiltinBlockIds.InputSelect,
			props: {
				options: [
					{ value: undefined, label: '$t:blocks.grid_layout.options.none' },
					{ value: 'top', label: '$t:blocks.grid_layout.options.top' },
					{ value: 'center', label: '$t:blocks.grid_layout.options.center' },
					{ value: 'bottom', label: '$t:blocks.grid_layout.options.bottom' },
				],
			},
		},
		{
			name: 'gridData',
			label: '$t:blocks.grid_layout.props.grid_data',
			render: LazyGridLayoutGridDataDebug,
			hidden: () => !gridLayoutBlockDebugFlag.value,
		},
	],
	slots: [
		{
			name: 'default',
			description: '$t:blocks.grid_layout.slots.default.description',
			showDefaultAddButton: true,
		},
	],
	overrideBlockDrag: true,
	interactive: true,
}</script>

<script lang="ts" setup>
import type { BlockSerializedChild, BlockSerializedSlotOutlet } from '#layers/block-engine/public';
import type { ResponsiveGridMode } from '#layers/blocks/composables/grid/grid-responsive';
import type { GridBlockPosition, GridLayoutData } from '#layers/blocks/composables/grid/grid-types';
import { breakpointsTailwind, createReusableTemplate, useElementSize } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { deepToRaw, gridLayoutBlockDebugFlag } from '#layers/base/public';
import { defineBlock, duplicateChildSubtree, isBlockSerializedChild, useBlockChild, useBlockDragHandler, useBlockEditorContext, useBlockInEditor, useBlockInEditorContext, useEditorBlockProp, useProvideBlockEditorChildBoundsRegistry } from '#layers/block-engine/public';
import { GRID_CONFIG } from '#layers/blocks/composables/grid/const';
import { validateNoOverlaps } from '#layers/blocks/composables/grid/grid-overlap';
import { getResponsiveGridLayout } from '#layers/blocks/composables/grid/grid-responsive';
import { cleanupEmptyRows, findAvailablePosition } from '#layers/blocks/composables/grid/grid-row-management';
import { useGridDragFromBlockBuilder } from '#layers/blocks/composables/grid/use-grid-drag-from-block-builder';
import { useGridOverlay } from '#layers/blocks/composables/grid/use-grid-overlay';
import { useGridResize } from '#layers/blocks/composables/grid/use-grid-resize';

interface GridLayoutProps {
	// Internal grid layout data (hidden from UI)
	gridData?: GridLayoutData;
	horizontalAlign?: 'top' | 'center' | 'bottom';
}

const props = defineProps<GridLayoutProps>();

;

// ============================================================================
// Editor Context
// ============================================================================

const inEditor = useBlockInEditor();
const { triggerLayoutUpdate, getSlot, moveBlock } = useBlockInEditorContext();
const {
	insertion,
	selection,
	block: editedBlock,
	updateBlock,
} = inEditor
	? useBlockEditorContext()
	: {
			insertion: null,
			selection: null,
			block: null,
			updateBlock: null,
		};
const getBlockChild = inEditor ? useBlockChild() : null;
const blockInstanceChildId = getBlockChild?.()?.id ?? null;
const containerRef = useTemplateRef<HTMLElement>('containerRef');
const { width: containerWidth } = useElementSize(containerRef);
const detectedResponsiveMode = computed<ResponsiveGridMode>(() => {
	if (containerWidth.value <= 0) {
		return 'full';
	}

	return getResponsiveGridModeForWidth(containerWidth.value);
});
const renderedResponsiveMode = computed<ResponsiveGridMode>(() => {
	if (inEditor) {
		return 'full';
	}

	return detectedResponsiveMode.value;
});

function getResponsiveGridModeForWidth(width: number): ResponsiveGridMode {
	if (width < breakpointsTailwind.sm) {
		return 'stacked';
	}

	if (width < breakpointsTailwind.md) {
		return 'paired';
	}

	return 'full';
}

// ============================================================================
// Template Refs
// ============================================================================

const rowRefs = ref<Record<number, HTMLElement>>({});
const childBoundsRegistry = inEditor ? useProvideBlockEditorChildBoundsRegistry() : null;

function setRowRef(rowIndex: number, el: HTMLElement | null) {
	if (el) {
		rowRefs.value[rowIndex] = el;
	}
	else {
		delete rowRefs.value[rowIndex];
	}
}

function setChildCellRef(childId: string, el: HTMLElement | null) {
	if (!childBoundsRegistry) return;

	childBoundsRegistry.setChildBoundsElement(childId, el);
}

// ============================================================================
// Grid Data Management
// ============================================================================

const gridDataProp = inEditor
	? useEditorBlockProp<GridLayoutData>('gridData')
	: computed({
			get: () => props.gridData,
			set: () => {
				throw new Error('Cannot set gridData in non-editor mode');
			},
		});

const gridDataActual = computed<GridLayoutData>(() => gridDataProp?.value ?? { rows: [], blockPositions: {} });

/**
 * Pending position for the next block that will be inserted.
 * Used by add buttons in available spaces to specify where the block should go.
 */
const pendingBlockPosition = ref<{ rowIndex: number; colStart: number; colSpan: number } | null>(null);

/**
 * We wait for the next tick after updating grid data to re-enable the watcher.
 * This prevents the watcher callback from being called with stale data that is not updated yet.
 */
let gridDataWatchEnabled = true;

function updateGridData(updates: Partial<GridLayoutData>) {
	if (!inEditor) return;
	gridDataProp.value = { ...gridDataActual.value, ...updates };
	gridDataWatchEnabled = false;

	nextTick(() => {
		gridDataWatchEnabled = true;
	});
}

// ============================================================================
// Editor Composables
// ============================================================================

const resize = inEditor ? useGridResize(gridDataActual, updateGridData) : null;
const drag = inEditor
	? useGridDragFromBlockBuilder(gridDataActual, updateGridData, containerRef, rowRefs, {
			moveBlock: (childId) => {
				moveBlock({ childId });
			},
			duplicateBlock: async (childId) => {
				if (!blockInstanceChildId || !editedBlock?.value || !updateBlock) {
					return null;
				}

				const parentChild = editedBlock.value.children?.[blockInstanceChildId];

				if (!parentChild || !isBlockSerializedChild(parentChild)) {
					return null;
				}

				const sourceChildren = deepToRaw(editedBlock.value.children ?? {}) as Record<
					string,
				BlockSerializedChild | BlockSerializedSlotOutlet
				>;
				const duplicatedChildren = {
					...sourceChildren,
				} as Record<string, BlockSerializedChild | BlockSerializedSlotOutlet>;
				const duplicatedRootId = duplicateChildSubtree(childId, sourceChildren, duplicatedChildren);

				if (!duplicatedRootId) {
					return null;
				}

				const defaultSlot = parentChild.slots.default?.slice() ?? [];
				defaultSlot.push(duplicatedRootId);
				const nextParentChild = structuredClone(deepToRaw(parentChild)) as BlockSerializedChild;

				nextParentChild.slots = {
					...nextParentChild.slots,
					default: defaultSlot,
				};

				updateBlock({
					children: {
						...duplicatedChildren,
						[blockInstanceChildId]: nextParentChild,
					},
				});

				return duplicatedRootId;
			},
		})
	: null;

const overlay = inEditor && drag && resize
	? useGridOverlay(gridDataActual, containerRef, rowRefs, {
			isDragging: drag.isDragging,
			isResizing: resize.isResizing,
			dragging: drag.dragging,
			resizing: resize.resizing,
			dragPreviewPosition: drag.previewPosition,
			resizePreviewPosition: resize.previewPosition,
			dropTarget: computed(() => drag.dragging.value?.dropTarget ?? null),
			betweenRowHover: drag.betweenRowHover,
		})
	: null;

if (inEditor && drag && blockInstanceChildId) {
	useBlockDragHandler(blockInstanceChildId, drag.overrideConfig);
}

// ============================================================================
// Rendered Grid Data
// ============================================================================

const renderedGridSource = computed<GridLayoutData>(() => {
	return drag?.previewGridData.value ?? gridDataActual.value;
});

const renderedGridData = computed<GridLayoutData>(() => {
	return getResponsiveGridLayout(renderedGridSource.value, {
		mode: renderedResponsiveMode.value,
	});
});

// ============================================================================
// Grid Position Helpers
// ============================================================================

interface AvailableSpace {
	rowIndex: number;
	colStart: number;
	colSpan: number;
}

interface RowItem {
	type: 'block' | 'space';
	colStart: number;
	childId?: string;
	space?: AvailableSpace;
}

/**
 * All items (blocks and available spaces) for each row, keyed by row index.
 * Computed once per renderedGridData change to avoid rebuilding on every render.
 */
const rowItems = computed<RowItem[][]>(() => {
	// Pre-bucket blocks by rowIndex to avoid O(rows × blocks) scanning
	const blocksByRow = new Map<number, Array<{ id: string; pos: GridBlockPosition }>>();

	for (const [id, pos] of Object.entries(renderedGridData.value.blockPositions)) {
		let bucket = blocksByRow.get(pos.rowIndex);

		if (!bucket) {
			bucket = [];
			blocksByRow.set(pos.rowIndex, bucket);
		}

		bucket.push({ id, pos });
	}

	return renderedGridData.value.rows.map((row, rowIndex) => {
		const blocks = (blocksByRow.get(rowIndex) ?? [])
			.toSorted((a, b) => a.pos.colStart - b.pos.colStart);

		const items: RowItem[] = [];
		let currentCol = 0;

		for (const block of blocks) {
			if (block.pos.colStart > currentCol) {
				items.push({
					type: 'space',
					colStart: currentCol,
					space: {
						rowIndex,
						colStart: currentCol,
						colSpan: block.pos.colStart - currentCol,
					},
				});
			}

			items.push({
				type: 'block',
				colStart: block.pos.colStart,
				childId: block.id,
			});

			currentCol = block.pos.colStart + block.pos.colSpan;
		}

		if (currentCol < row.columns) {
			items.push({
				type: 'space',
				colStart: currentCol,
				space: {
					rowIndex,
					colStart: currentCol,
					colSpan: row.columns - currentCol,
				},
			});
		}

		return items;
	});
});

/**
 * Handle click on an add button in an available space.
 * Sets the pending position and triggers the block picker.
 */
function addBlockAtPosition(space: AvailableSpace) {
	if (!insertion || !selection) return;

	queueBlockInsertionAtPosition({
		id: `add-grid-space-${space.rowIndex}-${space.colStart}`,
		position: space,
	});
}

function queueBlockInsertionAtPosition(options: {
	id: string;
	position: {
		rowIndex: number;
		colStart: number;
		colSpan: number;
	};
}) {
	if (!insertion || !selection) return;

	pendingBlockPosition.value = {
		rowIndex: options.position.rowIndex,
		colStart: options.position.colStart,
		colSpan: options.position.colSpan,
	};

	const child = getBlockChild?.();

	insertion.target.value = {
		id: options.id,
		childId: child?.id,
		slotName: 'default',
		index: getSlot('default').length,
	};

	selection.clear();
}

function isPendingInsertionTarget(target: { childId?: string; slotName?: string } | null) {
	return !!target
		&& target.childId === blockInstanceChildId
		&& target.slotName === 'default';
}

function addBlockInNewRow() {
	queueBlockInsertionAtPosition({
		id: `add-grid-new-row-${gridDataActual.value.rows.length}`,
		position: {
			rowIndex: gridDataActual.value.rows.length,
			colStart: 0,
			colSpan: GRID_CONFIG.COLUMNS,
		},
	});
}

// ============================================================================
// Row Styling
// ============================================================================

function getRowStyle(rowIndex: number, row: { columns: number }) {
	const style: Record<string, string> = {
		gridTemplateColumns: `repeat(${row.columns}, 1fr)`,
	};

	const lockedHeight = overlay?.lockedRowHeights.value?.[rowIndex];

	if (inEditor && drag?.isDragging.value && lockedHeight !== undefined) {
		style.minHeight = `${lockedHeight}px`;
	}

	return style;
}

// ============================================================================
// Child Block Management
// ============================================================================

function getChild(childId: string): BlockSerializedChild | null {
	return getSlot('default').find((c) => c.id === childId) ?? null;
}

// ============================================================================
// Watchers
// ============================================================================

watch(gridDataActual, () => {
	if (inEditor) triggerLayoutUpdate();
});

if (inEditor) {
	watch(
		() => insertion?.target.value ?? null,
		(target) => {
			if (pendingBlockPosition.value && !isPendingInsertionTarget(target)) {
				pendingBlockPosition.value = null;
			}
		},
	);

	watch(
		() => getSlot('default').map((child) => child.id),
		(childIds) => {
			childBoundsRegistry?.pruneChildBoundsElements(childIds);
		},
		{ immediate: true },
	);

	watch(
		() => getSlot('default'),
		(children: BlockSerializedChild[]) => {
			if (!gridDataProp || !gridDataWatchEnabled) return;

			const positions = { ...gridDataActual.value.blockPositions };
			const rows = [...gridDataActual.value.rows];
			let needsUpdate = false;

			for (const child of children) {
				if (!positions[child.id]) {
					// Use pending position if available, otherwise find first available
					const pending = pendingBlockPosition.value;
					let rowIndex: number;
					let colStart: number;
					let colSpan: number;
					let isNewRow: boolean;

					if (pending) {
						rowIndex = pending.rowIndex;
						colStart = pending.colStart;
						colSpan = pending.colSpan;
						isNewRow = rowIndex >= rows.length;
						pendingBlockPosition.value = null;
					}
					else {
						const found = findAvailablePosition(rows, positions);
						rowIndex = found.rowIndex;
						colStart = found.colStart;
						isNewRow = found.isNewRow;
						colSpan = isNewRow ? GRID_CONFIG.COLUMNS : 1;
					}

					if (isNewRow) {
						rows.push({ columns: GRID_CONFIG.COLUMNS });
					}

					positions[child.id] = {
						rowIndex,
						colStart,
						colSpan,
					};

					needsUpdate = true;
				}
			}

			const childIds = new Set(children.map((c) => c.id));

			for (const id of Object.keys(positions)) {
				if (!childIds.has(id)) {
					delete positions[id];
					needsUpdate = true;
				}
			}

			if (needsUpdate) {
				const result = cleanupEmptyRows(rows, positions);
				const finalRows = result.rows.length > 0 ? result.rows : rows;
				const finalPositions = result.positions;

				if (!validateNoOverlaps(finalPositions, finalRows).valid) {
					console.warn('[GridLayout] Rejected block position update: would cause overlap');
					return;
				}

				gridDataProp.value = {
					rows: finalRows,
					blockPositions: finalPositions,
				};
			}
		},
		{ deep: true },
	);
}

// ============================================================================
// Reusable Templates
// ============================================================================

const [CreateRenderEditorChild, RenderEditorChild] = createReusableTemplate({
	props: {
		child: { type: Object as PropType<BlockSerializedChild>, required: true },
	},
});

const [CreateRenderChild, RenderChild] = createReusableTemplate({
	props: {
		childId: { type: String, required: true },
	},
});

const [CreateRowSeparator, RowSeparator] = createReusableTemplate({
	props: {
		rowIndex: { type: Number, required: true },
	},
});
</script>

<template>
	<div ref="containerRef" class="group/grid-layout-block relative flex flex-col gap-(--block-spacing)">
		<CreateRenderEditorChild v-slot="{ child }">
			<BlockEditorSlotWrapper v-if="child">
				<BlockEditorRenderChild :child />
			</BlockEditorSlotWrapper>
		</CreateRenderEditorChild>

		<CreateRenderChild v-slot="{ childId }">
			<RenderEditorChild v-if="inEditor" :child="getChild(childId)!" />
			<BlockChildRender v-else :child-id />
		</CreateRenderChild>

		<CreateRowSeparator v-slot="{ rowIndex }">
			<div
				v-if="inEditor && drag?.isDragging.value"
				class="group/separator relative top-0.5 -my-3 flex h-4 items-center py-1 transition-all duration-100"
				:class="{
					'z-60 h-12! rounded-sm bg-primary/10': drag.betweenRowHover.value === rowIndex,
				}"
				:data-grid-row-separator="rowIndex === renderedGridData.rows.length ? 'append' : 'between'"
				@mouseenter="drag?.scheduleBetweenRowHover(rowIndex)"
				@mouseleave="drag?.clearBetweenRowHover()"
			>
				<!-- Separator line indicator -->
				<div
					class="mx-auto h-0.5 w-full rounded-full"
					:class="{
						'scale-y-200 bg-primary shadow-[0_0_10px_rgba(var(--color-primary-500),0.5)]': drag.betweenRowHover.value === rowIndex,
						'bg-primary/30': drag.betweenRowHover.value !== rowIndex,
					}"
				/>

				<!-- Preview indicator when hovering -->
				<div
					v-if="drag.betweenRowHover.value === rowIndex"
					class="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-center"
				>
					<div
						class="flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-inverted shadow-lg"
					>
						<DIcon name="lucide:plus" class="size-3" />
						<span>{{ $t('builder.insert_row_here') }}</span>
					</div>
				</div>
			</div>
		</CreateRowSeparator>

		<template v-if="gridDataActual.rows.length === 0">
			<!-- Empty state - show default slot (no live preview when empty) -->
			<div class="flex items-center justify-center rounded-lg border border-dashed border-default p-(--block-spacing)">
				<slot />
			</div>
		</template>
		<template v-else>
			<!-- Unified Overlay for all drag/resize previews -->
			<div v-if="overlay?.isOverlayActive.value" class="pointer-events-none absolute inset-0 z-50">
				<!-- Dim backdrop -->
				<div class="absolute inset-0 rounded-lg bg-black/5" />

				<!-- Column guides for active row -->
				<template v-for="guide in overlay.columnGuides.value" :key="`guide-${guide.rowIndex}-${guide.colIndex}`">
					<div
						class="border border-dashed"
						:class="{
							'border-primary/40 bg-primary/10': guide.isHighlighted,
							'border-muted bg-transparent': !guide.isHighlighted,
						}"
						:style="overlay.getColumnGuideStyle(guide.rowIndex, guide.colIndex)"
					/>
				</template>

				<!-- Preview block showing where it will land -->
				<template v-if="overlay.previewBlock.value && !overlay.previewBlock.value.isNewRow">
					<div
						class="flex items-center justify-center rounded-lg border-2 border-dashed"
						:class="{
							'border-primary bg-primary/15': overlay.previewBlock.value.isValid,
							'border-error bg-error/15': !overlay.previewBlock.value.isValid,
						}"
						:style="overlay.getPreviewStyle(
							overlay.previewBlock.value.rowIndex,
							overlay.previewBlock.value.colStart,
							overlay.previewBlock.value.colSpan,
						)"
					/>
				</template>
			</div>

			<!-- Render rows with positioned blocks -->
			<template v-for="(row, rowIndex) in renderedGridData.rows" :key="rowIndex">
				<RowSeparator :row-index />

				<!-- Grid row container -->
				<div class="group/row relative">
					<BlockMarkHorizontalLayout>
						<!-- Grid row -->
						<div
							:ref="(el) => setRowRef(rowIndex, el as HTMLElement)"
							class="relative grid w-full gap-(--block-spacing)"
							:class="{
								'items-start': props.horizontalAlign === 'top',
								'items-center': props.horizontalAlign === 'center',
								'items-end': props.horizontalAlign === 'bottom',
							}"
							data-grid-row
							:style="getRowStyle(rowIndex, row)"
						>
							<!-- Render blocks and available spaces in DOM order for correct CSS Grid placement -->
							<template
								v-for="item in rowItems[rowIndex]"
								:key="item.type === 'block' ? item.childId : `space-${item.space!.colStart}`"
							>
								<!-- Block -->
								<div
									v-if="item.type === 'block' && item.childId"
									:ref="(el) => setChildCellRef(item.childId!, el as HTMLElement | null)"
									class="group/cell relative"
									:data-grid-cell="item.childId"
									:class="{
										'scale-95 opacity-30': overlay?.draggedBlockId.value === item.childId && drag?.isDragging.value,
										'z-20': resize?.resizing.value?.childId === item.childId,
									}"
									:style="{
										gridColumn: `${renderedGridData.blockPositions[item.childId]!.colStart + 1} / span ${renderedGridData.blockPositions[item.childId]!.colSpan}`,
									}"
								>
									<!-- Resize handle - left -->
									<div
										v-if="inEditor && !drag?.isDragging.value"
										class="absolute inset-y-0 -left-1 z-30 w-3 cursor-col-resize opacity-0 group-hover/cell:opacity-100"
										data-block-editor-prevent-selection
										@mousedown="
											(e) => resize?.startResize(e, item.childId!, 'left', rowRefs[rowIndex]!)
										"
									>
										<!-- Visual indicator line -->
										<div
											class="
												absolute top-1/2 left-1/2 h-8 w-1.5 -translate-1/2 rounded-full border border-primary bg-white shadow-sm
											"
										/>
									</div>

									<!-- Block content -->
									<div
										class="relative size-full"
										:class="{
											'rounded-sm border border-dashed border-transparent group-hover/grid-layout-block:border-primary/50 group-hover/grid-layout-block:bg-primary/2': inEditor,
										}"
									>
										<RenderChild :child-id="item.childId" />
									</div>

									<!-- Resize handle - right -->
									<div
										v-if="inEditor && !drag?.isDragging.value"
										class="absolute inset-y-0 -right-1 z-30 w-3 cursor-col-resize opacity-0 group-hover/cell:opacity-100"
										data-block-editor-prevent-selection
										@mousedown="
											(e) => resize?.startResize(e, item.childId!, 'right', rowRefs[rowIndex]!)
										"
									>
										<!-- Visual indicator line -->
										<div
											class="
												absolute top-1/2 left-1/2 h-8 w-1.5 -translate-1/2 rounded-full border border-primary bg-white shadow-sm
											"
										/>
									</div>
								</div>

								<!-- Available space add button -->
								<DTooltip
									v-else-if="item.type === 'space' && item.space && inEditor && !drag?.isDragging.value"
									:text="$t('builder.add_a_block')"
								>
									<DButton
										icon="lucide:circle-plus"
										variant="ghost"
										color="neutral"
										block
										size="xs"
										class="
											invisible w-full self-stretch border border-dashed border-primary/50
											group-hover/grid-layout-block:visible
										"
										:style="{
											gridColumn: `${item.space.colStart + 1} / span ${item.space.colSpan}`,
										}"
										:data-grid-add-space="`${item.space.rowIndex}-${item.space.colStart}`"
										:aria-label="$t('builder.add_a_block')"
										data-block-editor-prevent-selection
										@click.stop="addBlockAtPosition(item.space)"
									/>
								</DTooltip>
							</template>
						</div>
					</BlockMarkHorizontalLayout>
				</div>
			</template>

			<RowSeparator :row-index="renderedGridData.rows.length" />

			<div v-if="inEditor" class="flex items-center justify-center">
				<DButton
					:label="$t('builder.add_block_in_new_row')"
					icon="lucide:circle-plus"
					variant="outline"
					color="neutral"
					size="xs"
					class="m-0.5"
					data-grid-add-new-row
					@click.stop="addBlockInNewRow"
				/>
			</div>
		</template>
	</div>
</template>
